import keymirror from 'keymirror';
import { normalize } from 'normalizr';
import { request } from 'js/api/axios';
import { callApi } from '../api';
import { SCHEMAS } from 'js/api/schemas';
import { spreadApiKeys, extractApiKeys } from '../utils';

export const types = keymirror({
  ...spreadApiKeys('LOAD_PRODUCT_MANAGER'),
  ...spreadApiKeys('LOAD_PRODUCT_MANAGERS'),
  ...spreadApiKeys('LOAD_CURRENT_USER_PRODUCT_MANAGER'),
  ...spreadApiKeys('CREATE_PRODUCT_MANAGER'),
  ...spreadApiKeys('UPDATE_PRODUCT_MANAGER'),
  ...spreadApiKeys('SEARCH_PRODUCT_MANAGERS'),
  ...spreadApiKeys('SEARCH_USERS_FOR_PRODUCT_MANAGER'),
});

export function listProductManagers(queryId = null) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_PRODUCT_MANAGERS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/product-managers`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.PRODUCT_MANAGER_ARRAY,
        meta: { queryId },
      },
      dispatch
    );
  };
}

export function loadProductManager(id, queryId = null) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_PRODUCT_MANAGER, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/product-managers/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.PRODUCT_MANAGER,
        meta: { queryId },
      },
      dispatch
    );
  };
}

export function loadCurrentUserProductManager(queryId = null) {
  return (dispatch, getState) => {
    const state = getState();
    const token = state.identity?.token;
    const workspaceId = state.currentWorkspaceId;
    if (!token || !workspaceId) return Promise.resolve();

    const [requestType, successType, errorType] = extractApiKeys(
      types.LOAD_CURRENT_USER_PRODUCT_MANAGER,
      types
    );
    const initialStatus = {
      isLoading: true,
      startedAt: Date.now(),
      finishedAt: null,
    };

    dispatch({
      type: requestType,
      status: initialStatus,
      meta: { queryId },
    });

    return request
      .get(`/api/workspaces/${workspaceId}/product-managers/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((resp) => {
        const data = resp.data?.data ?? resp.data;
        const normalized = normalize(data, SCHEMAS.PRODUCT_MANAGER);
        dispatch({
          type: successType,
          status: {
            ...initialStatus,
            isLoading: false,
            finishedAt: Date.now(),
          },
          data: normalized,
          meta: { queryId },
        });
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          dispatch({
            type: successType,
            status: {
              ...initialStatus,
              isLoading: false,
              finishedAt: Date.now(),
            },
            data: { result: null, entities: {} },
            meta: { queryId },
          });
          return;
        }
        dispatch({
          type: errorType,
          status: {
            ...initialStatus,
            isLoading: false,
            finishedAt: Date.now(),
          },
          error: err,
          meta: { queryId },
        });
      });
  };
}

export function createProductManager(productManager) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.CREATE_PRODUCT_MANAGER, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/product-managers`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { product_manager: productManager },
        schema: SCHEMAS.PRODUCT_MANAGER,
      },
      dispatch
    );
  };
}

export function updateProductManager(patch) {
  const { id, ...rest } = patch;
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.UPDATE_PRODUCT_MANAGER, types),
        request: (data, token, currentWorkspaceId) =>
          request.put(
            `/api/workspaces/${currentWorkspaceId}/product-managers/${id}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { product_manager: rest },
        meta: { patch: { productManagers: { [id]: rest } } },
        schema: SCHEMAS.PRODUCT_MANAGER,
      },
      dispatch
    );
  };
}

export const searchProductManagers = (query) => {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.SEARCH_PRODUCT_MANAGERS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/product-managers/search`,
            {
              params: { query },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.PRODUCT_MANAGER_ARRAY,
      },
      dispatch
    );
  };
};

export const searchUsersForProductManager = (query, userGroupId = null) => {
  return (dispatch) => {
    const params = { query };
    if (userGroupId != null) {
      params.user_group_id = userGroupId;
    }
    return callApi(
      {
        types: extractApiKeys(types.SEARCH_USERS_FOR_PRODUCT_MANAGER, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/product-managers/search`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
};
